import type { RestRequest } from 'msw'
import localforage from 'localforage'
import type { TListDataTypes } from './getListDataFunctionFactory'
import type { TEntityName } from '~/types/models'
import { getPublicRuntimeConfig } from '~/cynqueModules/nuxtAccessors'
import { useSettingsStore } from '~/stores/settings'
import { renameLegacyPersistedMockKeys } from './renameLegacyPersistedMockKeys'

const mn = '[api/msw/modules/mockDataStorageManager]'
localforage.config({ name: 'CynqueMock', storeName: 'mockData' })

function conditionalConsoleDebug(message: string) {
  if (
    typeof window !== 'undefined' &&
    getPublicRuntimeConfig().doLimitMockDataLogOutput
  ) {
    return
  }
  console.debug(message)
}

export async function storeEntityDataListToLocalForage({
  req,
  entityKeyBaseOverride,
  listData,
}: {
  req: RestRequest
  entityKeyBaseOverride?: TEntityName | undefined
  listData: TListDataTypes[]
}) {
  const fn = `${mn} storeEntityDataListToLocalForage |`
  const pathName = req.url.pathname
  if (!entityKeyBaseOverride) {
    console.debug(`${fn} pathName = '${pathName}`)
  }
  const entityKeyBase =
    entityKeyBaseOverride || getEntityStorageKeyBaseFromApiPath(pathName)
  if (!entityKeyBase) {
    throw new Error(`${fn} couldn't get entityKeyBase`)
  }
  console.debug(`${fn} entityKeyBase = '${entityKeyBase}'`)
  await _storeEntityDataListToLocalForage({
    entityKeyBase,
    listData,
    callingFn: fn,
  })
}

export async function _storeEntityDataListToLocalForage({
  entityKeyBase,
  listData,
  callingFn,
}: {
  entityKeyBase: string
  listData: TListDataTypes[]
  callingFn: string
}) {
  const fn = `${callingFn} --> ${mn} _storeEntityDataListToLocalForage |`
  if (!doPersistMockData()) {
    conditionalConsoleDebug(
      `${fn} doPersistMockData is false. Not storing in localForage`
    )
    return
  }
  try {
    conditionalConsoleDebug(`${fn} trying to store listData (${entityKeyBase})`)
    const entityKey = _getEntityKeyForBase(entityKeyBase)
    conditionalConsoleDebug(`${fn} key: (${entityKey})`)
    await localforage.setItem(entityKey, listData)
    conditionalConsoleDebug(`${fn} listData stored`)
    dispatchEventEntityDataInLocalForageChanged()
  } catch (error) {
    console.debug(`${fn} ${entityKeyBase}`, listData)
    console.error(error)
    throw new Error(`${fn} storing listData failed (${entityKeyBase})`)
  }
}

function dispatchEventEntityDataInLocalForageChanged() {
  window.dispatchEvent(
    new CustomEvent('entity-data-in-localforage-changed', {})
  )
}

export async function hasEntityDataInLocalForage(): Promise<boolean> {
  const arrayOfKeys = await localforage.keys()
  const list = arrayOfKeys.filter((key) => key.startsWith('esk_'))
  const hasData = !!list.length
  // console.debug(`${fn} returning ${hasData}`)
  return hasData
}

export async function getEntityListDataFromLocalForage<T>(
  entityKeyBase: string
) {
  const fn = `${mn} getEntityListDataFromLocalForage |`
  // console.debug(`${fn} entityKeyBase = '${entityKeyBase}'`)
  if (!doPersistMockData()) {
    conditionalConsoleDebug(
      `${fn} doPersistMockData is false. Not getting mock data from localForage`
    )
    return
  }

  const entityKey = _getEntityKeyForBase(entityKeyBase)
  try {
    const listData = (await localforage.getItem(entityKey)) as T
    if (!listData) {
      conditionalConsoleDebug(
        `${fn} entityKey = '${entityKey}' - no data in localForage`
      )
      return undefined
    }
    if (renameLegacyPersistedMockKeys(entityKeyBase, listData)) {
      console.info(`${fn} renamed legacy properties in '${entityKey}'`)
      await localforage.setItem(entityKey, listData)
    }
    return listData
  } catch (error) {
    throw new Error(`${fn} couldn't get or parse listData`)
  }
}

export function getEntityStorageKeyBaseFromApiPath(apiPath: string) {
  const fn = `${mn} getEntityStorageKeyBaseFromApiPath |`
  const entityKeyBase = apiPath?.split('/')[2]
  if (!entityKeyBase) {
    console.warn(`${fn} apiPath ${apiPath} has no element at index = 2.`)
    return null
  }
  // eslint-disable-next-line no-console
  conditionalConsoleDebug(`${fn} Returning entityKeyBase = '${entityKeyBase}'`)
  return entityKeyBase
}

export async function clearEntityDataInLocalForage(): Promise<boolean> {
  console.debug(`${mn} clearing entity data in localForage`)
  const arrayOfKeys = await localforage.keys()
  const list = arrayOfKeys.filter((key) => key.startsWith('esk_'))
  console.debug(list)
  if (list.length) {
    for (const key of list) {
      await localforage.removeItem(key)
    }
    dispatchEventEntityDataInLocalForageChanged()
    return true
  } else {
    console.debug(`${mn} no data to clear`)
    return false
  }
}

export const ENTITY_KEY_PREFIX = 'esk_'

function _getEntityKeyForBase(entityKeyBase: string) {
  return `${ENTITY_KEY_PREFIX}${entityKeyBase}`
}

function doPersistMockData(): boolean {
  return typeof window !== 'undefined' && useSettingsStore().doPersistMockData
    ? useSettingsStore().doPersistMockData
    : false
}
